/**
 * AlertConfigCard — Iter-91 · Operations Command Center config panel.
 *
 * Ops Directors + Admins can upsert per-region SMS routing here. Regional
 * Managers see their own region's row read-only. If a region has no row,
 * the backend falls back to the first `role=supervisor` in the region.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Phone, Save, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AlertConfigCard({ canEdit = false }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [draft, setDraft] = useState({});
  // Iter-95 · Company-wide Slack fallback.
  const [globalSlack, setGlobalSlack] = useState('');
  const [globalBusy, setGlobalBusy] = useState(false);

  const load = async () => {
    try {
      const [r, gr] = await Promise.all([
        fetch(`${API}/api/operations/alert-config`, { credentials: 'include' }),
        fetch(`${API}/api/operations/alert-settings/global`, { credentials: 'include' }),
      ]);
      if (r.ok) {
        const d = await r.json();
        setRows(d.regions || []);
        const seed = {};
        (d.regions || []).forEach((row) => {
          seed[row.region_id] = {
            primary_phone: row.primary_phone || '',
            escalation_phone: row.escalation_phone || '',
            delay_threshold_min: row.delay_threshold_min ?? 15,
            escalation_delay_min: row.escalation_delay_min ?? 15,
            slack_webhook_url: row.slack_webhook_url || '',
          };
        });
        setDraft(seed);
      }
      if (gr.ok) {
        const g = await gr.json();
        setGlobalSlack(g.slack_webhook_fallback_url || '');
      }
    } catch { /* silent */ }
  };

  useEffect(() => { load(); }, []);

  const save = async (region_id) => {
    setBusy(region_id);
    try {
      const d = draft[region_id] || {};
      const r = await fetch(`${API}/api/operations/alert-config`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          region_id,
          primary_phone: d.primary_phone,
          escalation_phone: d.escalation_phone || null,
          delay_threshold_min: Number(d.delay_threshold_min) || 15,
          escalation_delay_min: Number(d.escalation_delay_min) || 15,
          slack_webhook_url: d.slack_webhook_url || null,
        }),
      });
      if (r.ok) {
        toast.success('Alert routing saved');
        load();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
      }
    } catch (e) { toast.error(e.message); }
    finally { setBusy(null); }
  };

  const saveGlobal = async () => {
    setGlobalBusy(true);
    try {
      const r = await fetch(`${API}/api/operations/alert-settings/global`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slack_webhook_fallback_url: globalSlack || null }),
      });
      if (r.ok) toast.success('Global Slack fallback saved');
      else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || 'Save failed');
      }
    } catch (e) { toast.error(e.message); }
    finally { setGlobalBusy(false); }
  };

  return (
    <Card className="bg-zinc-900 border border-zinc-800 p-4" data-testid="ops-alert-config-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-[#C9A962]" />
          <h3 className="text-white font-black text-base" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            ALERT ROUTING · PER REGION
          </h3>
          <Badge className="bg-zinc-800 text-[10px] uppercase tracking-widest">
            {canEdit ? 'Editable' : 'Read-only'}
          </Badge>
        </div>
      </div>
      {/* Iter-95 · Company-wide Slack fallback webhook. Fires alongside
          SMS when a region has no Slack webhook configured of its own. */}
      <div className="mb-3 p-3 rounded-lg border border-purple-800/60 bg-purple-950/20" data-testid="ops-alert-global-slack">
        <div className="flex items-center justify-between mb-2">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold">
              Company-wide Slack fallback
            </p>
            <p className="text-[10px] text-zinc-500">
              Fires alongside SMS when a region has no Slack webhook of its own. Leave blank to disable.
            </p>
          </div>
          {canEdit && (
            <Button
              type="button"
              size="sm"
              onClick={saveGlobal}
              disabled={globalBusy}
              className="font-bold h-7 px-3"
              style={{ backgroundColor: '#a855f7', color: '#fff' }}
              data-testid="ops-alert-global-slack-save"
            >
              <Save className="h-3 w-3 mr-1" />
              {globalBusy ? 'Saving…' : 'Save Fallback'}
            </Button>
          )}
        </div>
        <Input
          disabled={!canEdit}
          value={globalSlack}
          onChange={(e) => setGlobalSlack(e.target.value)}
          placeholder="https://hooks.slack.com/services/T00000000/B00000000/XXXXXXXX"
          className="bg-zinc-900 border-zinc-700 text-white text-xs font-mono"
          data-testid="ops-alert-global-slack-input"
        />
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-zinc-500 italic">
          No regional routing configured yet. Alerts fall back to the region's first supervisor.
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div
              key={row.region_id}
              className="p-3 rounded-lg border border-zinc-800 bg-zinc-950"
              data-testid={`ops-alert-config-row-${row.region_id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold text-white">{row.region_name}</p>
                <span className="text-[10px] text-zinc-500 font-mono">{row.region_id}</span>
              </div>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-2">
                <Field label="Primary phone">
                  <Input
                    disabled={!canEdit}
                    value={(draft[row.region_id]?.primary_phone) ?? ''}
                    onChange={(e) => setDraft((s) => ({ ...s, [row.region_id]: { ...(s[row.region_id] || {}), primary_phone: e.target.value } }))}
                    placeholder="+18882413077"
                    className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    data-testid={`ops-alert-cfg-primary-${row.region_id}`}
                  />
                </Field>
                <Field label="Escalation phone">
                  <Input
                    disabled={!canEdit}
                    value={(draft[row.region_id]?.escalation_phone) ?? ''}
                    onChange={(e) => setDraft((s) => ({ ...s, [row.region_id]: { ...(s[row.region_id] || {}), escalation_phone: e.target.value } }))}
                    placeholder="+12149842834"
                    className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    data-testid={`ops-alert-cfg-escalation-${row.region_id}`}
                  />
                </Field>
                <Field label="Delay threshold (min)">
                  <Input
                    disabled={!canEdit}
                    type="number"
                    min={1} max={180}
                    value={(draft[row.region_id]?.delay_threshold_min) ?? 15}
                    onChange={(e) => setDraft((s) => ({ ...s, [row.region_id]: { ...(s[row.region_id] || {}), delay_threshold_min: e.target.value } }))}
                    className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    data-testid={`ops-alert-cfg-threshold-${row.region_id}`}
                  />
                </Field>
                <Field label="Escalation delay (min)">
                  <Input
                    disabled={!canEdit}
                    type="number"
                    min={1} max={180}
                    value={(draft[row.region_id]?.escalation_delay_min) ?? 15}
                    onChange={(e) => setDraft((s) => ({ ...s, [row.region_id]: { ...(s[row.region_id] || {}), escalation_delay_min: e.target.value } }))}
                    className="bg-zinc-900 border-zinc-700 text-white text-xs"
                    data-testid={`ops-alert-cfg-esc-delay-${row.region_id}`}
                  />
                </Field>
              </div>
              {/* Iter-95 · Per-region Slack webhook — fires alongside SMS. */}
              <div className="mt-2">
                <Field label="Slack webhook (optional · fires alongside SMS)">
                  <Input
                    disabled={!canEdit}
                    value={(draft[row.region_id]?.slack_webhook_url) ?? ''}
                    onChange={(e) => setDraft((s) => ({ ...s, [row.region_id]: { ...(s[row.region_id] || {}), slack_webhook_url: e.target.value } }))}
                    placeholder="https://hooks.slack.com/services/…"
                    className="bg-zinc-900 border-zinc-700 text-white text-xs font-mono"
                    data-testid={`ops-alert-cfg-slack-${row.region_id}`}
                  />
                </Field>
              </div>
              {canEdit && (
                <div className="flex justify-end mt-2">
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => save(row.region_id)}
                    disabled={busy === row.region_id}
                    className="font-bold h-7 px-3"
                    style={{ backgroundColor: '#C9A962', color: '#1a1a1a' }}
                    data-testid={`ops-alert-cfg-save-${row.region_id}`}
                  >
                    <Save className="h-3 w-3 mr-1" />
                    {busy === row.region_id ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              )}
              <p className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
                <Phone className="h-3 w-3" /> Numbers in E.164 format. Leave escalation blank to skip level-2.
              </p>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 font-semibold">{label}</p>
      {children}
    </div>
  );
}
